import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { authApi } from '@/api/auth'
import { useAuthStore } from '@/stores/authStore'
import { track } from '@/lib/analytics'

export function useAuth() {
  const { setTokens, setUser, logout, user, accessToken, activeOrgId, activeOrgRole, isImpersonating } =
    useAuthStore()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: authApi.getMe,
    enabled: !!accessToken,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (data) => {
      setTokens(data.access_token, data.refresh_token)
      queryClient.invalidateQueries({ queryKey: ['me'] })
      // GA4 funnel: returning-user sign-in completed. Not a primary
      // conversion, but useful for distinguishing new signups from
      // existing-user activity.
      track('login_completed')
      navigate('/dashboard')
    },
    onError: (e: any) => {
      // Funnel diagnostics: distinguish "tried and failed" from "never
      // attempted". The reason comes straight from the API's error
      // detail when available (e.g. "invalid_credentials"), else the
      // HTTP status, else "unknown".
      track('login_failed', {
        reason: e?.response?.data?.detail || e?.response?.status || 'unknown',
      })
    },
  })

  const registerMutation = useMutation({
    mutationFn: authApi.register,
    onSuccess: (data) => {
      setTokens(data.access_token, data.refresh_token)
      queryClient.invalidateQueries({ queryKey: ['me'] })
      // GA4 primary conversion event. Marked as a conversion in
      // GA4 Admin → Events so it shows up in attribution reports.
      track('signup_completed')
      navigate('/dashboard')
    },
    onError: (e: any) => {
      // Lets us measure the gap between signup_cta_click and
      // signup_completed. Common reasons we want to see: email already
      // exists, plan limit hit, invalid invite token.
      track('signup_failed', {
        reason: e?.response?.data?.detail || e?.response?.status || 'unknown',
      })
    },
  })

  const switchOrgMutation = useMutation({
    mutationFn: (orgId: string) => authApi.switchOrg(orgId),
    onSuccess: (data) => {
      setTokens(data.access_token, data.refresh_token)
      queryClient.clear()
      navigate('/dashboard')
    },
  })

  const handleLogout = () => {
    logout()
    queryClient.clear()
    navigate('/login')
  }

  return {
    user: me || user,
    accessToken,
    activeOrgId,
    activeOrgRole,
    isImpersonating,
    isAuthenticated: !!accessToken,
    login: loginMutation.mutate,
    loginError: loginMutation.error,
    isLoggingIn: loginMutation.isPending,
    register: registerMutation.mutate,
    registerError: registerMutation.error,
    isRegistering: registerMutation.isPending,
    switchOrg: switchOrgMutation.mutate,
    logout: handleLogout,
  }
}
